import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faChalkboardTeacher,
	faLayerGroup,
	faCalendar,
	faClock,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../common/Modal";
import FormGroup from "../../common/FormGroup";
import Select from "../../common/Select";
import SearchInput from "../../common/SearchInput";
import StudentList from "../../common/StudentList";
import StatDisplay from "../../common/StatDisplay";
import Notification from "../../common/Notification";
import Button from "../../common/Button";
import { useEditLogForm } from "./hooks/useEditLogForm";
import { CLASS_PERIODS } from "../../../config";
import styles from "./EditLogModal.module.css";

const EditLogModal = ({ isOpen, onClose, log, onUpdateSuccess }) => {
	const {
		lectureTitle,
		setLectureTitle,
		sequence,
		setSequence,
		selectedDay,
		setSelectedDay,
		period,
		setPeriod,
		absentStudents,
		studentOptions,
		weekDays,
		isLoading,
		notification,
		setNotification,
		handleSubmit,
		toggleStudentAbsence,
	} = useEditLogForm(log, onUpdateSuccess, onClose);

	const [searchTerm, setSearchTerm] = useState("");

	// Filter students based on search term
	const filteredStudents = studentOptions.filter((student) =>
		student.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const attendanceStats = [
		{
			label: "Present",
			value: studentOptions.length - absentStudents.length,
			variant: "success",
		},
		{
			label: "Absent",
			value: absentStudents.length,
			variant: "danger",
		},
		{
			label: "Total",
			value: studentOptions.length,
		},
	];

	const footer = (
		<>
			<div className={styles.footerLeft}>
				<StatDisplay stats={attendanceStats} />
			</div>
			<div className={styles.footerRight}>
				<Button variant="secondary" onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
					{isLoading ? "Saving..." : "Save Changes"}
				</Button>
			</div>
		</>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Class Log"
			size="large"
			footer={footer}
			loading={isLoading}
			className={styles.editLogModal}
		>
			{notification && (
				<div className={styles.notifications}>
					<Notification
						type="error"
						message={notification}
						onClose={() => setNotification("")}
					/>
				</div>
			)}

			<div className={styles.modalContent}>
				{/* Left column - Log Information */}
				<div className={styles.logInfoColumn}>
					<div className={styles.sectionHeader}>
						<h3>Class Information</h3>
					</div>

					<div className={styles.formFields}>
						<FormGroup label="Day of the Week" icon={faCalendar}>
							<Select
								value={selectedDay}
								onChange={(e) => setSelectedDay(e.target.value)}
								options={weekDays}
								placeholder="Select a day"
							/>
						</FormGroup>

						<FormGroup label="Period" icon={faClock}>
							<Select
								value={period}
								onChange={(e) => setPeriod(e.target.value)}
								options={CLASS_PERIODS}
								placeholder="Select a period"
							/>
						</FormGroup>

						<FormGroup label="Lecture Title" icon={faChalkboardTeacher}>
							<input
								type="text"
								className={styles.textInput}
								placeholder="Enter lecture title"
								value={lectureTitle}
								onChange={(e) => setLectureTitle(e.target.value)}
							/>
						</FormGroup>

						<FormGroup label="Sequence Number" icon={faLayerGroup}>
							<input
								type="text"
								className={styles.textInput}
								placeholder="Enter sequence number"
								value={sequence}
								onChange={(e) => setSequence(e.target.value)}
							/>
						</FormGroup>

						{/* Mobile-only attendance summary */}
						<div className={styles.mobileOnly}>
							<div className={styles.summarySection}>
								<h4>Attendance Summary</h4>
								<StatDisplay stats={attendanceStats} />
							</div>
						</div>
					</div>
				</div>

				{/* Right column - Student Attendance */}
				<div className={styles.attendanceColumn}>
					<div className={styles.sectionHeader}>
						<h3>Student Attendance</h3>
					</div>

					<div className={styles.studentSearch}>
						<SearchInput
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder="Search students..."
						/>
					</div>

					<div className={styles.studentsListContainer}>
						<StudentList
							students={filteredStudents}
							absentStudents={absentStudents}
							onToggleAbsence={toggleStudentAbsence}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default EditLogModal;
